// 通道表单的草稿与换算，字段见 docs/03 3.5。连接栏逐项填写（#184）：SMPP 协议参数的字段同 internal/smpp 的 ChannelParams（决策 D28），
// 对接兼容参数在"高级"栏，留空即缺省、不写进 config（D151）；
// TLS（D51）、熔断参数（D48）各按自己的结构拼回 JSON；bind 只填最大数量（D86，暂时只有一个出口实例，D157）。
// Telegram 通道（protocol=http、adapter=telegram，D207）的参数同 internal/httpcfg 的 Telegram，逐项填写；回执能力锁定为支持，不连 bind。
// 适配器认不出的旧 HTTP 通道只能改通用字段，config 原样 JSON 带回（#187、#405）。
// 错误码映射还没有定义格式、connector 也不读，表单不显示，已有的值原样带回。通道不带币种，币种以价格为准（决策 D85）。
import type { Errors } from "@/app/useDraft";
import { formatJson, intIn, joinLines, parseIntList, parseJson, parsePositive, splitLines } from "@/app/form";
import { channelKind, type Channel, type ChannelKind, type ChannelProtocol, type EntityStatus, type Input } from "@/app/master";

/** 六个能力标记，供路由硬过滤。 */
export const CAPABILITIES: { key: CapabilityKey; label: string }[] = [
  { key: "dlr_supported", label: "支持 DLR" },
  { key: "dlr_trusted", label: "DLR 可信" },
  { key: "validity_period_supported", label: "支持有效期" },
  { key: "idempotency_key_supported", label: "支持幂等键" },
  { key: "custom_sender_supported", label: "支持自定义 sender" },
  { key: "unicode_supported", label: "支持 Unicode" },
];
export type CapabilityKey = "dlr_supported" | "dlr_trusted" | "validity_period_supported" | "idempotency_key_supported" | "custom_sender_supported" | "unicode_supported";

/** 熔断参数的字段、默认值与是否为 0 到 1 的比例（决策 D48）；只看提交阶段，送达率不参与（D90）；半开试探固定每分钟一条，不按通道配置（D88）；恢复后慢启动的三个参数（D89）。 */
export const CIRCUIT_FIELDS: { key: CircuitKey; label: string; fallback: string; ratio: boolean }[] = [
  { key: "min_samples", label: "最少样本数", fallback: "50", ratio: false },
  { key: "error_rate", label: "提交错误率上限", fallback: "0.5", ratio: true },
  { key: "cooldown_sec", label: "熔断冷却（秒）", fallback: "60", ratio: false },
  { key: "probe_min_samples", label: "半开最少样本数", fallback: "10", ratio: false },
  { key: "ramp_start_tps", label: "慢启动起始 TPS", fallback: "1", ratio: false },
  { key: "ramp_factor", label: "慢启动倍数", fallback: "2", ratio: false },
  { key: "ramp_interval_sec", label: "慢启动间隔（秒）", fallback: "60", ratio: false },
];
export type CircuitKey = "min_samples" | "error_rate" | "cooldown_sec" | "probe_min_samples" | "ramp_start_tps" | "ramp_factor" | "ramp_interval_sec";

/** SMPP 协议参数，字段同 internal/smpp 的 ChannelParams。 */
export interface SMPPDraft { host: string; port: string; system_id: string; password: string; system_type: string; address_range: string; enquire_link_interval: string }

/** SMPP 对接兼容参数（决策 D151）。空串表示缺省：不写进 config，connector 按原来的行为发送。 */
export interface CompatDraft {
  dest_ton: string; dest_npi: string; source_ton: string; source_npi: string;
  dest_format: string; country_code: string; concat: string;
  gsm7_dcs: string; latin1: string; force_ucs2: boolean;
}

/** 合法的 TON 与 NPI（SMPP 3.4 第 5.2.5、5.2.6 节），与服务端校验一致。 */
export const TON_OPTIONS = [["0", "0 未知"], ["1", "1 国际"], ["2", "2 国内"], ["3", "3 网络特定"], ["4", "4 用户号码"], ["5", "5 字母数字"], ["6", "6 缩写"]] as const;
export const NPI_OPTIONS = [["0", "0 未知"], ["1", "1 ISDN（E.164）"], ["3", "3 数据（X.121）"], ["4", "4 电传"], ["6", "6 陆地移动（E.212）"], ["8", "8 国家"], ["9", "9 私有"], ["10", "10 ERMES"], ["14", "14 互联网（IP）"], ["18", "18 WAP 客户端 ID"]] as const;

/** Telegram 通道的参数，键同 internal/httpcfg 的 Telegram（docs/03 3.5）。空串表示缺省、不写进 config；token 编辑时为打码值。 */
export interface TelegramDraft { token: string; ttl: string; code_pattern: string; sender_username: string; timeout: string; base_url: string }

/** TTL 的取值范围（秒），同 httpcfg.MinTTL、MaxTTL。 */
export const TTL_MIN = 30, TTL_MAX = 3600;

/** TLS 设置（决策 D51）。verify 缺省为真，关掉要显式写 false。 */
export interface TLSDraft { enabled: boolean; verify: boolean; ca: string; client_cert: string; client_key: string; server_name: string }

export interface ChannelDraft extends Record<CapabilityKey, boolean> {
  supplier_id: string; name: string; protocol: ChannelProtocol; status: EntityStatus; manually_removed: boolean;
  /** 界面上的通道类型：新建时选 SMPP 或 Telegram，编辑时不可改；提交时据此写 protocol 与 adapter。 */
  kind: ChannelKind;
  /** 编辑已有通道：凭证留空或保持打码表示不改（D43）。 */
  existing: boolean;
  telegram: TelegramDraft;
  smpp: SMPPDraft; compat: CompatDraft; tls: TLSDraft; circuit: Record<CircuitKey, string>; max_binds: string;
  /** 熔断开关，默认关（决策 D91）。 */
  circuit_enabled: boolean;
  /** 旧 HTTP 通道的参数，原样 JSON。 */
  config: string;
  /** SMPP 或 Telegram 参数里表单不认识的键，原样带回，不静默丢掉。 */
  config_extra: Record<string, unknown>;
  error_map: unknown;
  adapter: string; dlr_source_ips: string; webhook_token: string;
  tps: string; window_size: string; max_submit_retries: string; retry_backoff_ms: string; max_length: string;
  senders: string; route_type: string;
}

/** 最大 bind 数的上限：上游的 bind 配额远小于它，只挡住手误（决策 D86）。 */
const MAX_BINDS = 100;

const SMPP_KEYS: (keyof SMPPDraft)[] = ["host", "port", "system_id", "password", "system_type", "address_range", "enquire_link_interval"];
const TELEGRAM_KEYS: (keyof TelegramDraft)[] = ["token", "ttl", "code_pattern", "sender_username", "timeout", "base_url"];
const COMPAT_KEYS = ["dest_ton", "dest_npi", "source_ton", "source_npi", "dest_format", "country_code", "concat", "data_coding"];

function asObject(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

function str(v: unknown): string {
  return v === undefined || v === null ? "" : String(v);
}

/** 显式写成缺省值的兼容参数归一成空串，下拉框显示为"缺省"那一项（D151）。 */
function orDefault(v: unknown, fallback: string): string {
  const s = str(v);
  return s === fallback ? "" : s;
}

function smppDraft(config: unknown): { smpp: SMPPDraft; compat: CompatDraft; extra: Record<string, unknown> } {
  const c = asObject(config);
  const dc = asObject(c.data_coding);
  const smpp = Object.fromEntries(SMPP_KEYS.map((k) => [k, str(c[k])])) as unknown as SMPPDraft;
  const compat: CompatDraft = {
    dest_ton: str(c.dest_ton), dest_npi: str(c.dest_npi), source_ton: str(c.source_ton), source_npi: str(c.source_npi),
    dest_format: orDefault(c.dest_format, "e164"), country_code: str(c.country_code), concat: orDefault(c.concat, "udh8"),
    gsm7_dcs: orDefault(dc.gsm7_dcs, "0"), latin1: orDefault(dc.latin1, "latin1"), force_ucs2: dc.force_ucs2 === true,
  };
  const known = [...SMPP_KEYS, ...COMPAT_KEYS] as string[];
  const extra = Object.fromEntries(Object.entries(c).filter(([k]) => !known.includes(k)));
  return { smpp, compat, extra };
}

function telegramDraft(config: unknown): { telegram: TelegramDraft; extra: Record<string, unknown> } {
  const c = asObject(config);
  const telegram = Object.fromEntries(TELEGRAM_KEYS.map((k) => [k, str(c[k])])) as unknown as TelegramDraft;
  if (telegram.ttl === "0") telegram.ttl = ""; // 0 即不设，显示为空
  const extra = Object.fromEntries(Object.entries(c).filter(([k]) => !(TELEGRAM_KEYS as string[]).includes(k)));
  return { telegram, extra };
}

function tlsDraft(tls: unknown): TLSDraft {
  const t = asObject(tls);
  return { enabled: t.enabled === true, verify: t.verify !== false, ca: str(t.ca), client_cert: str(t.client_cert), client_key: str(t.client_key), server_name: str(t.server_name) };
}

function circuitDraft(circuit: unknown): Record<CircuitKey, string> {
  const c = asObject(circuit);
  return Object.fromEntries(CIRCUIT_FIELDS.map((f) => [f.key, c[f.key] ? String(c[f.key]) : ""])) as Record<CircuitKey, string>;
}

export function channelDraft(row?: Channel): ChannelDraft {
  if (!row) {
    return {
      supplier_id: "", name: "", protocol: "smpp", status: "active", manually_removed: false, kind: "smpp", existing: false, telegram: telegramDraft({}).telegram,
      smpp: smppDraft({ port: 2775, enquire_link_interval: "30s" }).smpp, compat: smppDraft({}).compat, tls: tlsDraft({}), circuit: circuitDraft({}), circuit_enabled: false, max_binds: "1",
      config: "{}", config_extra: {}, error_map: {}, adapter: "", dlr_source_ips: "", webhook_token: "",
      tps: "50", window_size: "10", max_submit_retries: "5", retry_backoff_ms: "1000,5000,30000", max_length: "1000", senders: "", route_type: "",
      dlr_supported: true, dlr_trusted: true, validity_period_supported: true, idempotency_key_supported: false, custom_sender_supported: true, unicode_supported: true,
    };
  }
  const kind = channelKind(row);
  const { smpp, compat, extra: smppExtra } = smppDraft(kind === "smpp" ? row.config : {});
  const { telegram, extra: telegramExtra } = telegramDraft(kind === "telegram" ? row.config : {});
  return {
    supplier_id: String(row.supplier_id), name: row.name, protocol: row.protocol, status: row.status, manually_removed: row.manually_removed, kind, existing: true, telegram,
    smpp, compat, tls: tlsDraft(row.tls), circuit: circuitDraft(row.circuit), circuit_enabled: asObject(row.circuit).enabled === true, max_binds: String(row.max_binds ?? 0),
    config: kind === "smpp" || kind === "telegram" ? "{}" : formatJson(row.config), config_extra: kind === "telegram" ? telegramExtra : smppExtra, error_map: row.error_map ?? {},
    adapter: row.adapter ?? "", dlr_source_ips: joinLines(row.dlr_source_ips), webhook_token: row.webhook_token ?? "",
    tps: String(row.tps), window_size: String(row.window_size), max_submit_retries: String(row.max_submit_retries), retry_backoff_ms: (row.retry_backoff_ms ?? []).join(","),
    max_length: String(row.max_length), senders: joinLines(row.senders), route_type: row.route_type ?? "",
    dlr_supported: row.dlr_supported, dlr_trusted: row.dlr_trusted, validity_period_supported: row.validity_period_supported,
    idempotency_key_supported: row.idempotency_key_supported, custom_sender_supported: row.custom_sender_supported, unicode_supported: row.unicode_supported,
  };
}

/** 草稿的字段错误；连接栏的子字段用"组.字段"作键，例如 smpp.host、circuit.error_rate。 */
export type ChannelErrors = Errors<ChannelDraft> & Record<string, string | undefined>;

/** Go 的 time.ParseDuration 能解析、且不为负的时长，例如 0（不发心跳）、30s、1m30s、.5s。 */
const DURATION = /^\+?(0|((\d+\.?\d*|\.\d+)(ns|us|µs|ms|s|m|h))+)$/;

/** SMPP 参数拼回 JSON：必填项与格式不对时记错误；可选项留空就不写。 */
function smppConfig(d: ChannelDraft, errors: ChannelErrors): Record<string, unknown> {
  const s = d.smpp;
  const port = Number(s.port);
  if (!s.host.trim()) errors["smpp.host"] = "填写上游地址。";
  if (!/^\d+$/.test(s.port.trim()) || port < 1 || port > 65535) errors["smpp.port"] = "1 到 65535 的整数。";
  if (!s.system_id.trim()) errors["smpp.system_id"] = "填写 system_id。";
  if (s.enquire_link_interval.trim() && !DURATION.test(s.enquire_link_interval.trim())) errors["smpp.enquire_link_interval"] = "时长，例如 30s、1m。";
  const out: Record<string, unknown> = { ...d.config_extra, host: s.host.trim(), port, system_id: s.system_id.trim() };
  if (s.password) out.password = s.password; // 密码原样提交，首尾空格也是密码的一部分
  for (const k of ["system_type", "address_range", "enquire_link_interval"] as const) {
    if (s[k].trim()) out[k] = s[k].trim();
  }
  return out;
}

/** 正的 Go 时长：DURATION 之外还要有非零数字，httpcfg 拒绝 0s。 */
function positiveDuration(v: string): boolean {
  return DURATION.test(v) && /[1-9]/.test(v);
}

/** Telegram 参数拼回 config（D207 第 2 条，规则同 httpcfg）：留空的不写；token 新建必填，编辑时留空不写、打码值原样提交，由服务端沿用原值（D43）。
 * 取码正则由服务端按 Go 的语法校验，前端不用 JS 的正则去猜。 */
function telegramConfig(d: ChannelDraft, errors: ChannelErrors): Record<string, unknown> {
  const t = d.telegram;
  const token = t.token.trim(), ttl = t.ttl.trim(), timeout = t.timeout.trim(), baseURL = t.base_url.trim();
  if (!d.existing && (!token || token === "****")) errors["telegram.token"] = "填写 API token。";
  if (ttl && ttl !== "0" && intIn(ttl, TTL_MIN, TTL_MAX) == null) errors["telegram.ttl"] = `${TTL_MIN} 到 ${TTL_MAX} 的整数；留空不设。`;
  if (timeout && !positiveDuration(timeout)) errors["telegram.timeout"] = "正的时长，例如 10s、1m。";
  if (baseURL && !httpURL(baseURL)) errors["telegram.base_url"] = "http 或 https 地址，例如 https://gatewayapi.telegram.org/。";
  const out: Record<string, unknown> = { ...d.config_extra };
  if (token) out.token = token;
  if (ttl && ttl !== "0") out.ttl = Number(ttl);
  for (const k of ["code_pattern", "sender_username"] as const) {
    if (t[k].trim()) out[k] = t[k].trim();
  }
  if (timeout) out.timeout = timeout;
  if (baseURL) out.base_url = baseURL;
  return out;
}

function httpURL(v: string): boolean {
  try {
    const u = new URL(v);
    return (u.protocol === "http:" || u.protocol === "https:") && u.host !== "";
  } catch {
    return false;
  }
}

/** 按通道类型拼 config：旧 HTTP 通道与黑洞通道原样带回。 */
function configOf(d: ChannelDraft, errors: ChannelErrors): unknown {
  if (d.kind === "smpp") return { ...smppConfig(d, errors), ...compatConfig(d, errors) };
  if (d.kind === "telegram") return telegramConfig(d, errors);
  return parseJson(d.config, {});
}

/** 提交的 protocol 与 adapter：Telegram 是 http + telegram（D207），其余沿用。 */
function protocolOf(d: ChannelDraft): { protocol: ChannelProtocol; adapter: string | null } {
  if (d.kind === "smpp") return { protocol: "smpp", adapter: null };
  if (d.kind === "telegram") return { protocol: "http", adapter: "telegram" };
  return { protocol: d.protocol, adapter: d.adapter.trim() || null };
}

/** 兼容参数拼回 config：留空的不写；国家码只在 national 时写；会转成 UCS-2 的映射要求通道支持 Unicode（D151，规则同服务端）。 */
function compatConfig(d: ChannelDraft, errors: ChannelErrors): Record<string, unknown> {
  const c = d.compat;
  const out: Record<string, unknown> = {};
  for (const k of ["dest_ton", "dest_npi", "source_ton", "source_npi"] as const) {
    if (c[k]) out[k] = Number(c[k]);
  }
  if (c.dest_format) out.dest_format = c.dest_format;
  if (c.dest_format === "national") {
    if (!/^\d{1,4}$/.test(c.country_code.trim())) errors["compat.country_code"] = "national 格式要填国家码，1 到 4 位数字，例如 62。";
    else out.country_code = c.country_code.trim();
  }
  if (c.concat) out.concat = c.concat;
  const dc: Record<string, unknown> = {};
  // 只有非缺省值才算与 force_ucs2 矛盾，同服务端
  const dcs1 = c.gsm7_dcs !== "" && c.gsm7_dcs !== "0", latin1UCS2 = c.latin1 !== "" && c.latin1 !== "latin1";
  if (dcs1) dc.gsm7_dcs = Number(c.gsm7_dcs);
  if (latin1UCS2) dc.latin1 = c.latin1;
  if (c.force_ucs2) dc.force_ucs2 = true;
  if (c.force_ucs2 && (dcs1 || latin1UCS2)) errors["compat.force_ucs2"] = "一律 UCS-2 时 GSM7 与 Latin-1 的发法不再生效，先改回缺省。";
  else if ((c.force_ucs2 || c.latin1 === "ucs2") && !d.unicode_supported) errors["compat.force_ucs2"] = "会把内容转成 UCS-2，先在能力里勾选支持 Unicode。";
  if (Object.keys(dc).length) out.data_coding = dc;
  return out;
}

/** TLS 设置拼回 JSON：只写开着的开关、关掉的校验与填了的路径；客户端证书与私钥要成对（D51）。 */
function tlsConfig(t: TLSDraft, errors: ChannelErrors): Record<string, unknown> {
  // TLS 关着时这些字段不显示，错误没处可看，只在打开时校验；关着时已存的值原样带回
  if (t.enabled && !t.client_cert.trim() !== !t.client_key.trim()) errors["tls.client_key"] = "客户端证书与私钥要同时填写。";
  const out: Record<string, unknown> = {};
  if (t.enabled) out.enabled = true;
  if (!t.verify) out.verify = false;
  for (const k of ["ca", "client_cert", "client_key", "server_name"] as const) {
    if (t[k].trim()) out[k] = t[k].trim();
  }
  return out;
}

/** 熔断参数拼回 JSON：留空取默认不写；计数与秒数是非负整数，比例在 0 到 1 之间（D48）。 */
function circuitConfig(c: Record<CircuitKey, string>, errors: ChannelErrors): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of CIRCUIT_FIELDS) {
    const raw = c[f.key].trim();
    if (!raw) continue;
    const v = Number(raw);
    const ok = f.ratio ? Number.isFinite(v) && v >= 0 && v <= 1 : /^\d+$/.test(raw);
    if (!ok) errors[`circuit.${f.key}`] = f.ratio ? "0 到 1 的小数，例如 0.5。" : "非负整数。";
    else if (f.key === "ramp_factor" && (v === 1 || v > 100)) errors[`circuit.${f.key}`] = "2 到 100 的整数；1 会让限额永远不涨。"; // 同服务端校验（决策 D89）
    else out[f.key] = v;
  }
  return out;
}

export function channelInput(d: ChannelDraft): { body?: Input<Channel>; errors: ChannelErrors } {
  const errors: ChannelErrors = {};
  const config = configOf(d, errors);
  // 非 SMPP 通道的表单不显示 bind 与 TLS（#187），错误没处可看，不拦保存，已存的值原样带回
  const smpp = d.kind === "smpp", telegram = d.kind === "telegram";
  const tls = tlsConfig(d.tls, smpp ? errors : {});
  // 熔断关着时参数不显示，错误没处可看，不拦保存，已存的值原样带回；开着才写 enabled（决策 D91）
  const circuit = { ...(d.circuit_enabled ? { enabled: true } : {}), ...circuitConfig(d.circuit, d.circuit_enabled ? errors : {}) };
  const maxBinds = intIn(d.max_binds, 0, MAX_BINDS);
  const tps = parsePositive(d.tps);
  const window = parsePositive(d.window_size);
  const retries = /^\d+$/.test(d.max_submit_retries.trim()) ? Number(d.max_submit_retries) : null;
  const backoff = parseIntList(d.retry_backoff_ms);
  const maxLength = parsePositive(d.max_length);
  if (!d.supplier_id) errors.supplier_id = "选择供应商。";
  if (!d.name.trim()) errors.name = "填写通道名称。";
  if (config === null) errors.config = "不是合法的 JSON。";
  if (maxBinds == null && smpp) errors.max_binds = `0 到 ${MAX_BINDS} 的整数，0 表示不连接。`;
  if (tps == null) errors.tps = "正整数。";
  if (window == null) errors.window_size = "正整数。";
  if (retries == null) errors.max_submit_retries = "非负整数。";
  if (backoff == null) errors.retry_backoff_ms = "逗号分隔的毫秒数。";
  if (maxLength == null) errors.max_length = "正整数。";
  if (Object.values(errors).some(Boolean) || tps == null || window == null || retries == null || backoff == null || maxLength == null) return { errors };
  return {
    errors,
    body: {
      supplier_id: Number(d.supplier_id), name: d.name.trim(), ...protocolOf(d), status: d.status, manually_removed: d.manually_removed,
      // webhook_token 新建由服务端生成；编辑时打码值原样带回表示不改，Telegram 的 null 表示重新生成（D209）
      config, tls, error_map: d.error_map, circuit, dlr_source_ips: splitLines(d.dlr_source_ips), webhook_token: d.webhook_token.trim() || null,
      // Telegram 不连 bind，max_binds 不用（docs/03 3.5）；回执能力必须为真，否则快照校验拒绝（D207 第 6 条）
      max_binds: telegram ? 0 : maxBinds ?? 0, tps, window_size: window, max_submit_retries: retries, retry_backoff_ms: backoff, max_length: maxLength,
      senders: splitLines(d.senders), route_type: d.route_type.trim() || null,
      dlr_supported: telegram || d.dlr_supported, dlr_trusted: d.dlr_trusted, validity_period_supported: d.validity_period_supported,
      idempotency_key_supported: d.idempotency_key_supported, custom_sender_supported: d.custom_sender_supported, unicode_supported: d.unicode_supported,
    },
  };
}
